import { PlatformError, type DocStorePort, type DocTxOps, type DocTxRunner } from '@djgg/core';
import { actionLogDoc, creationDoc, versionDoc, assetVariantDoc, assetGenJobDoc } from '../paths.js';
import { compositionBundleSlotsV1, compositionMediaSlotKindV1 } from '../spec/compositionPresentation.js';
import { emptyWorkshop, workshopDoc, workshopSlot, type WorkshopCarryNotice, type WorkshopSelection, type WorkshopState, type ShowEvent } from '../workshop.js';
import type { Creation, CreationAsset, AssetVariantDoc, CreationVersion, AssetGenJob } from '../types.js';
import { AssetRejectionsRepo } from './assetRejections.js';

export class WorkshopRepo {
  constructor(private readonly store: DocStorePort & Partial<DocTxRunner>) {}
  async get(creationId: string): Promise<WorkshopState> {
    return (await this.store.getDoc<{ state: WorkshopState }>(workshopDoc(creationId)))?.state ?? emptyWorkshop();
  }
  /** Bounded first-occurrence telemetry; never changes a saved choice revision. */
  async recordShowEvent(creationId: string, version: number, event: ShowEvent, at: number, authorize: (creation: Creation) => void): Promise<boolean> {
    if (!this.store.runTransaction) return false;
    return this.store.runTransaction(async tx => {
      const creation = await tx.get<Creation>(creationDoc(creationId));
      if (!creation) return false;
      authorize(creation);
      if (!Number.isSafeInteger(version) || version < 0 || version > creation.latestVersion) return false;
      const state = (await tx.get<{ state: WorkshopState }>(workshopDoc(creationId)))?.state ?? emptyWorkshop();
      const key = `${version}:${event}`;
      if (state.showEvents?.[key] !== undefined) return false;
      state.showEvents = Object.fromEntries([...Object.entries(state.showEvents ?? {}), [key, at]].slice(-70));
      await tx.set(workshopDoc(creationId), { state });
      return true;
    });
  }
  /** Effective inventory for an explicit artwork publish. Called again under
   * acceptAssetRebind's creation lock so stale tabs cannot mutate a newer game. */
  async inventory(creationId: string, origin: number, revision: number, actor: string, handle: string, tx?: DocTxOps): Promise<CreationAsset[]> {
    const get = <T>(path: string) => tx ? tx.get<T>(path) : this.store.getDoc<T>(path);
    const creation = await get<Creation>(creationDoc(creationId));
    if (!creation) throw new PlatformError('not-found', 'creation not found');
    if (actor !== creation.creatorUid && !creation.memberUids?.includes(actor)) throw new PlatformError('permission', 'creation membership changed');
    const state = (await get<{ state: WorkshopState }>(workshopDoc(creationId)))?.state ?? emptyWorkshop();
    if (state.revision !== revision) throw new PlatformError('conflict', 'Workshop choices changed');
    let version = creation.activeVersion;
    for (let depth = 0; version !== origin && depth < 10; depth++) {
      const current = await get<CreationVersion>(versionDoc(creationId, version));
      if (!current?.artCompletionOf) throw new PlatformError('conflict', 'A newer gameplay build needs its own artwork choices');
      version = current.artCompletionOf;
    }
    if (version !== origin) throw new PlatformError('conflict', 'Artwork origin is no longer current');
    let assets = [...creation.assets ?? []];
    for (const selected of state.selections.filter(s => s.originVersion === origin)) {
      const v = await get<AssetVariantDoc>(assetVariantDoc(creationId, selected.variantId));
      if (!v || v.creationId !== creationId || v.reviewState === 'rejected' || v.reviewState === 'superseded') throw new PlatformError('conflict', 'Selected artwork is no longer eligible');
      if (v.kind === 'cover') continue; // Covers use the cover workflow, never game-bundle publication.
      await new AssetRejectionsRepo(this.store).assertAllowed(creationId, v.contentSha256, creation.assetPolicyRevision ?? 0, tx);
      assets = assets.filter(a => workshopSlot(a) !== selected.slot);
      assets.push({ variantId: v.variantId, kind: v.kind, name: v.name, url: v.url, prompt: v.prompt,
        attachedBy: actor, attachedByHandle: handle, attachedAt: selected.selectedAt,
        ...(v.contentSha256 ? { contentSha256: v.contentSha256 } : {}), ...(v.audio ? { audio: v.audio } : {}), ...(v.strip ? { strip: v.strip } : {}) });
    }
    const bases = new Set(assets.filter(a => a.kind === 'sprite').map(a => a.variantId));
    return assets.filter(a => !a.strip || bases.has(a.strip.referenceVariantId));
  }

  /** Runs inside the composed go-live transaction: a failed revision never gets
   * here. Code moves each saved choice onto the new gameplay version when its
   * slot and variant still hold there; otherwise it drops it with a notice. An
   * artwork publication keeps its gameplay origin, so it moves nothing. */
  async carryToLiveVersion(tx: DocTxOps, input: {
    creationId: string; creation: Creation; atMs: number;
    version: Pick<CreationVersion, 'n' | 'updatedBy' | 'updatedByHandle' | 'artCompletionOf' | 'executionPolicy'>;
  }): Promise<void> {
    const { creationId, creation, version, atMs } = input;
    if (version.artCompletionOf) return;
    const current = (await tx.get<{ state: WorkshopState }>(workshopDoc(creationId)))?.state;
    const older = current?.selections.filter(s => s.originVersion < version.n) ?? [];
    if (!current || !older.length) return;
    const kept = current.selections.filter(s => s.originVersion >= version.n);
    const claimed = new Set(kept.map(s => s.slot));
    // Newest origin wins a slot; a run that failed still left a real choice.
    const newest = new Map<string, WorkshopSelection>();
    for (const s of [...older].sort((a, b) => a.originVersion - b.originVersion)) if (!claimed.has(s.slot)) newest.set(s.slot, s);
    const slots = new Set(compositionBundleSlotsV1(version.executionPolicy?.composition?.media?.slots ?? [])
      .map(slot => workshopSlot({ kind: compositionMediaSlotKindV1(slot), name: slot })));
    const nextRevision = current.revision + 1;
    const dropped: WorkshopCarryNotice[] = [];
    const drop = (s: WorkshopSelection, reason: WorkshopCarryNotice['reason']) => dropped.push({ version: version.n, slot: s.slot, variantId: s.variantId, reason, at: atMs });
    const survivors: Array<{ selection: WorkshopSelection; variant: AssetVariantDoc }> = [];
    for (const selection of newest.values()) {
      if (!selection.slot.startsWith('cover:') && !slots.has(selection.slot)) { drop(selection, 'slot-gone'); continue; }
      const variant = await this.carriableVariant(tx, creationId, selection.variantId, creation);
      if (variant) survivors.push({ selection, variant }); else drop(selection, 'variant-gone');
    }
    const selectedSprites = [...kept.filter(s => s.slot.startsWith('sprite:')), ...survivors.map(s => s.selection).filter(s => s.slot.startsWith('sprite:'))];
    const replaced = new Set(selectedSprites.map(s => s.slot));
    const bases = new Set([...selectedSprites.map(s => s.variantId),
      ...(creation.assets ?? []).filter(a => a.kind === 'sprite' && !replaced.has(workshopSlot(a))).map(a => a.variantId)]);
    const carried: WorkshopSelection[] = [];
    for (const { selection, variant } of survivors) {
      if (variant.strip && !bases.has(variant.strip.referenceVariantId)) { drop(selection, 'base-gone'); continue; }
      const { snapshotVersion: _published, ...rest } = selection;
      carried.push({ ...rest, originVersion: version.n, revision: nextRevision });
    }
    const state: WorkshopState = { ...current, revision: nextRevision, selections: [...kept, ...carried],
      carryNotices: [...current.carryNotices ?? [], ...dropped].slice(-20) };
    await tx.set(workshopDoc(creationId), { state });
    await tx.set(actionLogDoc(`workshop-carry-${creationId}-${version.n}`), {
      action: 'workshop.carry', creationId, slug: creation.slug, title: creation.title,
      updatedBy: version.updatedBy, updatedByHandle: version.updatedByHandle,
      actionPerformedBy: version.updatedBy, actionPerformedByHandle: version.updatedByHandle, createdAt: atMs,
      details: { version: version.n, carried: carried.map(s => s.slot),
        dropped: dropped.map(({ slot, variantId, reason }) => ({ slot, variantId, reason })) },
    });
  }

  /** The same eligibility inventory() enforces at Apply, as a verdict. */
  private async carriableVariant(tx: DocTxOps, creationId: string, variantId: string, creation: Creation): Promise<AssetVariantDoc | null> {
    const variant = await tx.get<AssetVariantDoc>(assetVariantDoc(creationId, variantId));
    if (!variant || variant.creationId !== creationId || variant.reviewState === 'rejected' || variant.reviewState === 'superseded') return null;
    if (variant.kind === 'cover') return variant;
    try {
      await new AssetRejectionsRepo(this.store).assertAllowed(creationId, variant.contentSha256, creation.assetPolicyRevision ?? 0, tx);
    } catch (error) {
      if (error instanceof PlatformError) return null;
      throw error;
    }
    return variant;
  }

  async save(input: {
    creationId: string; originVersion: number; expectedRevision: number; actor: string;
    variantId?: string; draft?: string; now: number;
    authorize: (creation: Creation) => void;
  }): Promise<{ state: WorkshopState; stale: boolean }> {
    if (!this.store.runTransaction) throw new PlatformError('unavailable', 'workshop requires transactions');
    return this.store.runTransaction(async tx => {
      const creation = await tx.get<Creation>(creationDoc(input.creationId));
      if (!creation) throw new PlatformError('not-found', 'creation not found');
      input.authorize(creation);
      const current = (await tx.get<{ state: WorkshopState }>(workshopDoc(input.creationId)))?.state ?? emptyWorkshop();
      if (current.revision !== input.expectedRevision) return { state: current, stale: true };
      const origin = await tx.get<CreationVersion>(versionDoc(input.creationId, input.originVersion));
      if (!origin) throw new PlatformError('not-found', 'origin build not found');
      const state = structuredClone(current);
      state.revision++;
      if (input.variantId) {
        const v = await tx.get<AssetVariantDoc>(assetVariantDoc(input.creationId, input.variantId));
        if (!v || v.name === 'title-overlay' || v.creationId !== input.creationId || !['sprite', 'background', 'strip', 'cover', 'icon', 'music', 'sfx'].includes(v.kind)
          || v.reviewState === 'rejected' || v.reviewState === 'superseded') {
          throw new PlatformError('validation', 'candidate is not eligible');
        }
        const job = v.genJobId ? await tx.get<AssetGenJob>(assetGenJobDoc(v.genJobId)) : null;
        const knownSlot = v.kind === 'cover' || (creation.assets ?? []).some(a => workshopSlot(a) === workshopSlot(v))
          || (job?.creationId === input.creationId && job.completion?.originVersion === input.originVersion);
        if (!knownSlot) throw new PlatformError('validation', 'candidate has no compatible slot in this build');
        await new AssetRejectionsRepo(this.store).assertAllowed(input.creationId, v.contentSha256, creation.assetPolicyRevision ?? 0, tx);
        if (v.strip) {
          const selectedBases = state.selections.filter(s => s.originVersion === input.originVersion && s.slot.startsWith('sprite:'));
          const replaced = new Set(selectedBases.map(s => s.slot));
          const bases = [...selectedBases.map(s => s.variantId),
            ...(creation.assets ?? []).filter(a => a.kind === 'sprite' && !replaced.has(workshopSlot(a))).map(a => a.variantId)];
          if (!v.strip.referenceVariantId || !bases.includes(v.strip.referenceVariantId)) throw new PlatformError('validation', 'animation belongs to a different character');
        }
        const slot = workshopSlot(v);
        state.selections = state.selections.filter(s => s.originVersion !== input.originVersion || s.slot !== slot);
        state.selections.push({ originVersion: input.originVersion, slot, variantId: v.variantId,
          revision: state.revision, actor: input.actor, selectedAt: input.now });
      }
      if (input.draft !== undefined) state.drafts[String(input.originVersion)] = input.draft;
      await tx.set(workshopDoc(input.creationId), { state });
      return { state, stale: false };
    });
  }
}
